/**
 * JobProgress route.
 *
 * After the new-job wizard flow:
 *   NewJob submit → DepositModal → navigate here
 *
 * Even in the happy path above, the user may *skip* the in-wizard
 * DepositModal (or close the browser mid-flow and come back later). In
 * that case the job row on the server is still in `awaiting_deposit`
 * and we need to re-surface the deposit entry points here, so the user
 * doesn't get stranded staring at a blank polling page.
 *
 * We therefore:
 *   - Poll /api/jobs/:token every 15s for the job row + splits + recent txs
 *   - When `job.status === "awaiting_deposit"`, render a prominent
 *     `cp-deposit-prompt` card with the amount + address + two big
 *     buttons (QR, Connect wallet) that open <DepositModal/>.
 *   - `?deposit=qr` / `?deposit=wc` query params let the wizard deep-link
 *     directly into a specific sub-view — currently unused but wired so
 *     future tweaks don't require the user to click twice.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { QrCode, Link as LinkIcon } from "lucide-react";

import type { JobProgressResponse } from "@u2u/shared";

import { abortJob, getJob } from "../lib/api.js";
import {
  DepositModal,
  type DepositModalView,
} from "../components/DepositModal.js";

const POLL_MS = 15_000;

export function JobProgress() {
  const { token = "" } = useParams<{ token: string }>();
  const loc = useLocation();
  const nav = useNavigate();
  const [state, setState] = useState<JobProgressResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // `?deposit=qr|wc|deeplink` opens the DepositModal directly on mount
  // (used by future email/SMS reminders that deep-link into one option).
  const initialDepositView = useMemo<DepositModalView | null>(() => {
    const q = new URLSearchParams(loc.search).get("deposit");
    if (q === "qr" || q === "wc" || q === "deeplink" || q === "menu") {
      return q;
    }
    return null;
  }, [loc.search]);

  const [depositOpen, setDepositOpen] = useState(initialDepositView !== null);
  const [depositView, setDepositView] = useState<DepositModalView>(
    initialDepositView ?? "menu",
  );

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const next = await getJob(token);
        if (alive) setState(next);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    };
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [token]);

  if (err) return <div className="cp-page"><div className="cp-shell"><div className="cp-error">Error: {err}</div></div></div>;
  if (!state) return <div className="cp-page"><div className="cp-shell"><div className="cp-hint">Loading job…</div></div></div>;
  const { job, splits, recent_transactions } = state;
  const awaitingDeposit = job.status === "awaiting_deposit";

  return (
    <div className="cp-page">
      <div className="cp-shell">
        <div className="cp-row-between">
          <h1 className="cp-h1">// job progress</h1>
          <button
            type="button"
            className="cp-btn cp-btn--small"
            onClick={() => nav("/")}
          >
            dashboard
          </button>
        </div>

        <div className="cp-frame">
          <span className="cp-corner-tr" />
          <span className="cp-corner-bl" />
          <div className="cp-stack">
            <div>
              <span className="cp-label">Job ID</span>
              <div className="cp-mono-line">{job.id}</div>
            </div>
            <div className="cp-inline-stats">
              <div>
                <span className="cp-label">Status</span>
                <span
                  className={
                    job.status === "failed"
                      ? "cp-error"
                      : awaitingDeposit
                        ? "cp-status-amber"
                        : "cp-ok"
                  }
                >
                  {job.status}
                </span>
              </div>
              <div>
                <span className="cp-label">Current step</span>
                <code className="cp-mono-inline">{job.current_step}</code>
              </div>
            </div>
            {job.last_error && (
              <p className="cp-error">Last error: {job.last_error}</p>
            )}
          </div>
        </div>

        {awaitingDeposit && (
          <div className="cp-deposit-prompt">
            <div className="cp-deposit-prompt-head">
              <h2 className="cp-h2">// awaiting deposit</h2>
              <p className="cp-hint">
                Send <strong className="cp-deposit-prompt-amount">
                  {job.amount_usdt.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  USDT (TRC20)
                </strong>{" "}
                to the wallet A address below to start the flow.
              </p>
              <div className="cp-mono-line cp-deposit-prompt-addr">
                {job.source_tron_address}
              </div>
            </div>
            <div className="cp-deposit-prompt-actions">
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                onClick={() => {
                  setDepositView("menu");
                  setDepositOpen(true);
                }}
              >
                <LinkIcon size={12} /> Deposit options
              </button>
              <button
                type="button"
                className="cp-btn"
                onClick={() => {
                  setDepositView("qr");
                  setDepositOpen(true);
                }}
              >
                <QrCode size={12} /> Show QR
              </button>
            </div>
          </div>
        )}

        <div className="cp-frame">
          <span className="cp-corner-tr" />
          <span className="cp-corner-bl" />
          <h3 className="cp-h3">// splits ({splits.length})</h3>
          <table className="cp-fee-table">
            <thead>
              <tr>
                <th className="cp-fee-th-hop">#</th>
                <th className="cp-fee-th-num">Proportion</th>
                <th className="cp-fee-th-num">Delay</th>
                <th className="cp-fee-th-desc">Destination</th>
                <th className="cp-fee-th-num">Status</th>
              </tr>
            </thead>
            <tbody>
              {splits.map((s) => (
                <tr key={s.index}>
                  <td className="cp-fee-td-hop">{s.index + 1}</td>
                  <td className="cp-fee-td-num">
                    {(s.amount_proportion * 100).toFixed(1)}%
                  </td>
                  <td className="cp-fee-td-num">
                    {(s.delay_seconds / 3600).toFixed(1)}h
                  </td>
                  <td className="cp-fee-td-desc">
                    <code className="cp-mono-inline">
                      {s.destination_tron_address.slice(0, 8)}…
                    </code>
                  </td>
                  <td className="cp-fee-td-num">{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cp-frame">
          <span className="cp-corner-tr" />
          <span className="cp-corner-bl" />
          <h3 className="cp-h3">// recent transactions</h3>
          {recent_transactions.length === 0 ? (
            <p className="cp-hint">No transactions yet.</p>
          ) : (
            <table className="cp-fee-table">
              <thead>
                <tr>
                  <th className="cp-fee-th-desc">When</th>
                  <th className="cp-fee-th-num">Step</th>
                  <th className="cp-fee-th-num">Chain</th>
                  <th className="cp-fee-th-num">Kind</th>
                  <th className="cp-fee-th-desc">Hash</th>
                </tr>
              </thead>
              <tbody>
                {recent_transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="cp-fee-td-desc">{t.created_at}</td>
                    <td className="cp-fee-td-num">{t.step}</td>
                    <td className="cp-fee-td-num">{t.chain}</td>
                    <td className="cp-fee-td-num">{t.kind}</td>
                    <td className="cp-fee-td-desc">
                      {t.tx_hash ? (
                        <code className="cp-mono-inline">
                          {t.tx_hash.slice(0, 10)}…
                        </code>
                      ) : (
                        <span className="cp-hint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            className="cp-btn cp-btn--danger cp-btn--small"
            onClick={() => abortJob(token).catch((e) => setErr(e.message))}
          >
            Abort
          </button>
        </div>
      </div>

      {depositOpen && (
        <DepositModal
          walletAddress={job.source_tron_address}
          amountUsdt={job.amount_usdt}
          initialView={depositView}
          onDeposited={() => setDepositOpen(false)}
          onDismiss={() => setDepositOpen(false)}
        />
      )}
    </div>
  );
}
